import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/client';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { DeleteRuleModal } from './DeleteRuleModal';
import {
  useCreateUserAccess,
  useDeleteUserAccess,
  useUserAccessDealers,
  useUserAccessList,
  useUserAccessOptions,
  userDisplay,
  type UserAccessRule,
  type UserAccessScope,
} from './useUserAccess';

import '@/styles/stla/user-access.scss';

/**
 * Replaces `Pages/Stellantis/UserAccessAdmin.cshtml`.
 *
 * Rules decide which dealers a non-Admin account may see; Admins need none. The
 * list is paginated over distinct users, not rule rows, because the table runs
 * to five figures in production and a user's rules have to stay together.
 *
 * Validation is deliberately not react-hook-form here. Three of the four ways
 * this form can fail — unknown user, missing scope value for the chosen scope,
 * duplicate rule — are only knowable on the server, so a client schema would
 * restate `required` and nothing else. Server messages are keyed by field and
 * land under the input they belong to, which the Razor page's single status
 * banner could not do.
 */
export function UserAccessPage() {
  const { t } = useTranslation('mystery/dealerlist');

  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);

  // A narrower filter can leave the current page past the end; the server clamps
  // and reports where it landed, but asking for page 1 avoids the empty flash.
  useEffect(() => setPage(1), [search]);

  const list = useUserAccessList(page, search);

  return (
    <div className="uac-page">
      <h1 className="uac-title">{t('UserAccess.Title', 'User Access — Mystery Calls')}</h1>
      <p className="uac-subtitle">
        {t(
          'UserAccess.Subtitle',
          'Assign which data each user can see. Multiple rules for the same user are combined.',
        )}
      </p>

      <CreateRuleCard />

      <div className="uac-card">
        <div className="uac-toolbar">
          <h3 style={{ margin: 0 }}>{t('UserAccess.Existing', 'Existing rules')}</h3>
          <div className="uac-search-form">
            <input
              type="search"
              className="form-control form-control-sm"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={t('UserAccess.SearchPlaceholder', 'Filter by user, country, dealer…')}
              aria-label={t('UserAccess.SearchPlaceholder', 'Filter by user, country, dealer…')}
            />
          </div>
          <span className="uac-count">
            {t('UserAccess.Counts', '{{rules}} rules · {{users}} users', {
              rules: list.data?.totalRules ?? 0,
              users: list.data?.totalUsers ?? 0,
            })}
          </span>
        </div>

        {list.isPending ? (
          <p className="uac-empty">{t('UserAccess.Loading', 'Loading…')}</p>
        ) : list.isError ? (
          <p className="uac-empty">
            {t('UserAccess.LoadFailed', 'The rules could not be loaded. Try again.')}
          </p>
        ) : list.data.groups.length === 0 ? (
          <p className="uac-empty">
            {search
              ? t('UserAccess.NoMatches', 'No rules match the filter.')
              : t('UserAccess.NoRules', 'No rules configured. Non-Admin users see no data.')}
          </p>
        ) : (
          <>
            {list.data.groups.map((group) => (
              <UserGroup
                key={group.userId}
                userId={group.userId}
                fullName={group.fullName}
                email={group.email}
                rules={group.rules}
              />
            ))}

            {list.data.totalPages > 1 && (
              <div className="uac-pagination">
                <button
                  type="button"
                  className={`uac-btn uac-btn-ghost${page <= 1 ? ' uac-btn-disabled' : ''}`}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  disabled={page <= 1}
                >
                  &laquo; {t('UserAccess.Previous', 'Previous')}
                </button>
                <span className="uac-count">
                  {t('UserAccess.PageOf', 'Page {{page}} of {{total}}', {
                    page: list.data.page,
                    total: list.data.totalPages,
                  })}
                </span>
                <button
                  type="button"
                  className={`uac-btn uac-btn-ghost${
                    page >= list.data.totalPages ? ' uac-btn-disabled' : ''
                  }`}
                  onClick={() => setPage((current) => current + 1)}
                  disabled={page >= list.data.totalPages}
                >
                  {t('UserAccess.Next', 'Next')} &raquo;
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** The create form and its status banner. */
function CreateRuleCard() {
  const { t } = useTranslation('mystery/dealerlist');
  const userListId = useId();
  const dealerListId = useId();

  const [userIdentifier, setUserIdentifier] = useState('');
  const [scopeType, setScopeType] = useState<UserAccessScope>('Country');
  const [countryCode, setCountryCode] = useState('');
  const [vatCode, setVatCode] = useState('');
  const [status, setStatus] = useState<string | null>(null);

  const options = useUserAccessOptions();
  // Thousands of rows: only worth fetching once the Dealer scope is chosen.
  const dealers = useUserAccessDealers(scopeType === 'Dealer');
  const create = useCreateUserAccess();

  const fieldErrors = useMemo(() => normalizeFieldErrors(create.error), [create.error]);

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setStatus(null);

    create.mutate(
      {
        userIdentifier,
        scopeType,
        countryCode: scopeType === 'Country' ? countryCode : null,
        vatCode: scopeType === 'Dealer' ? vatCode : null,
      },
      {
        onSuccess: () => {
          setStatus(
            t('UserAccess.CreatedMessage', "Rule created for user '{{user}}'.", { user: userIdentifier }),
          );
          setCountryCode('');
          setVatCode('');
        },
      },
    );
  };

  // A failure without field keys (a 500, a dropped connection) still has to say
  // something; the keyed ones are shown under their input instead.
  const generalError =
    create.isError && Object.keys(fieldErrors).length === 0
      ? create.error instanceof ApiError
        ? create.error.message
        : t('UserAccess.CreateFailed', 'The rule could not be created.')
      : null;

  return (
    <div className="uac-card">
      <h3>{t('UserAccess.CreateTitle', 'Create new rule')}</h3>
      <p className="uac-card-help">
        {t(
          'UserAccess.CreateHelp',
          'Country: the user only sees dealers of the chosen country code. Dealer: the user only sees the dealer with the chosen VAT code. Admin users see all data without any rule.',
        )}
      </p>

      {status && <div className="uac-status">{status}</div>}
      {generalError && <div className="uac-status error">{generalError}</div>}

      <form onSubmit={onSubmit}>
        <div className="uac-grid">
          <div className="uac-field">
            <label htmlFor={`${userListId}-input`}>{t('UserAccess.User', 'User')}</label>
            <input
              id={`${userListId}-input`}
              list={userListId}
              value={userIdentifier}
              onChange={(event) => setUserIdentifier(event.target.value)}
              className={`form-control form-control-sm${fieldErrors.useridentifier ? ' is-invalid' : ''}`}
              placeholder={t('UserAccess.UserPlaceholder', 'Search by name or email…')}
              autoComplete="off"
              required
            />
            <datalist id={userListId}>
              {options.data?.users.map((user) => (
                <option key={user.id} value={user.email}>
                  {userDisplay(user)}
                </option>
              ))}
            </datalist>
            {fieldErrors.useridentifier ? (
              <div className="error">{fieldErrors.useridentifier}</div>
            ) : (
              <div className="hint">
                {t(
                  'UserAccess.UserHint',
                  'Start typing a name or email and select the user from the list.',
                )}
              </div>
            )}
          </div>

          <div className="uac-field">
            <label htmlFor={`${userListId}-scope`}>{t('UserAccess.Scope', 'Scope')}</label>
            <select
              id={`${userListId}-scope`}
              className="form-select form-select-sm"
              value={scopeType}
              onChange={(event) => setScopeType(event.target.value as UserAccessScope)}
            >
              <option value="Country">{t('UserAccess.ScopeCountry', 'Country')}</option>
              <option value="Dealer">{t('UserAccess.ScopeDealer', 'Dealer')}</option>
            </select>
          </div>

          {/* Third column of the same row rather than a grid of its own, so it
              lines up with the two fields it belongs with. Only the field the
              selected scope uses is rendered — the Razor page toggled `display`
              on both. */}
          {scopeType === 'Country' ? (
            <div className="uac-field">
              <label htmlFor={`${userListId}-country`}>
                {t('UserAccess.CountryCode', 'Country code')}
              </label>
              <select
                id={`${userListId}-country`}
                value={countryCode}
                onChange={(event) => setCountryCode(event.target.value)}
                className={`form-select form-select-sm${fieldErrors.countrycode ? ' is-invalid' : ''}`}
              >
                <option value="">—</option>
                {options.data?.countryCodes.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
              {fieldErrors.countrycode && <div className="error">{fieldErrors.countrycode}</div>}
            </div>
          ) : (
            <div className="uac-field">
              <label htmlFor={`${dealerListId}-input`}>{t('UserAccess.VatCode', 'VAT code')}</label>
              <input
                id={`${dealerListId}-input`}
                list={dealerListId}
                value={vatCode}
                onChange={(event) => setVatCode(event.target.value)}
                className={`form-control form-control-sm${fieldErrors.vatcode ? ' is-invalid' : ''}`}
                placeholder={t('UserAccess.VatCodePlaceholder', 'dealer VAT code')}
                autoComplete="off"
              />
              <datalist id={dealerListId}>
                {dealers.data?.map((dealer) => (
                  <option key={dealer.vatCode} value={dealer.vatCode}>
                    {dealer.companyName} ({dealer.countryCode})
                  </option>
                ))}
              </datalist>
              {fieldErrors.vatcode && <div className="error">{fieldErrors.vatcode}</div>}
            </div>
          )}
        </div>

        <div className="uac-actions">
          <button type="submit" className="uac-btn uac-btn-primary" disabled={create.isPending}>
            {create.isPending
              ? t('UserAccess.Creating', 'Creating…')
              : t('UserAccess.CreateButton', 'Create rule')}
          </button>
        </div>
      </form>
    </div>
  );
}

/** One user's rules, with the delete confirmation they share. */
function UserGroup({
  userId,
  fullName,
  email,
  rules,
}: {
  userId: string;
  fullName: string | null;
  email: string | null;
  rules: UserAccessRule[];
}) {
  const { t } = useTranslation('mystery/dealerlist');
  const [pending, setPending] = useState<UserAccessRule | null>(null);
  const remove = useDeleteUserAccess();

  return (
    <div className="uac-user-group">
      <div className="uac-user-head">
        {fullName ? (
          <>
            <span className="uac-user-name">{fullName}</span>
            <span className="uac-user-mail">{email}</span>
          </>
        ) : email ? (
          <span className="uac-user-name">{email}</span>
        ) : (
          <span className="uac-user-name uac-muted">
            {t('UserAccess.UnknownUser', 'Unknown user')}
          </span>
        )}
        <span className="uac-user-id" title="user_id">
          {userId}
        </span>
      </div>

      <table className="uac-table">
        <thead>
          <tr>
            <th style={{ width: '110px' }}>{t('UserAccess.Scope', 'Scope')}</th>
            <th>{t('UserAccess.Value', 'Value')}</th>
            <th>{t('UserAccess.Created', 'Created')}</th>
            <th style={{ width: '90px' }} />
          </tr>
        </thead>
        <tbody>
          {rules.map((rule) => (
            <tr key={rule.id}>
              <td>
                <span
                  className={`scope-badge ${rule.scopeType === 'Country' ? 'scope-country' : 'scope-dealer'}`}
                >
                  {rule.scopeType}
                </span>
              </td>
              <td>{rule.scopeValue}</td>
              <td className="uac-muted">{formatCreatedAt(rule.createdAt)}</td>
              <td>
                <button
                  type="button"
                  className="uac-btn uac-btn-danger"
                  onClick={() => setPending(rule)}
                >
                  {t('UserAccess.Delete', 'Delete')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {pending && (
        <DeleteRuleModal
          rule={pending}
          pending={remove.isPending}
          onCancel={() => setPending(null)}
          onConfirm={() => remove.mutate(pending.id, { onSuccess: () => setPending(null) })}
        />
      )}
    </div>
  );
}

/**
 * `yyyy-MM-dd HH:mm`, matching the Razor format string. The value is UTC, and
 * the Razor page rendered it as stored rather than converting, so this does the
 * same — a rule's audit timestamp shifting by the reader's offset would be worse
 * than one that is consistently UTC.
 */
function formatCreatedAt(iso: string): string {
  return iso.replace('T', ' ').slice(0, 16);
}

/**
 * `ValidationProblemDetails.errors` keyed by lower-cased field name, with the
 * messages for a field joined. ASP.NET keys them by the C# parameter name
 * (`UserIdentifier`), and matching that casing at every call site is one typo
 * away from silently dropping an error.
 */
function normalizeFieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError)) return {};

  const result: Record<string, string> = {};
  for (const [field, messages] of Object.entries(error.fieldErrors)) {
    if (messages.length > 0) result[field.toLowerCase()] = messages.join(' ');
  }
  return result;
}
